import { auth } from '../firebase';
import { Action, ActionCreate, ActionUpdate, ActionListResponse, FileUploadResponse, SignedUrlResponse, BatchSignedUrlsResponse } from '../types/action';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

class ActionsService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  // List all actions for a device
  async listActions(deviceId: string): Promise<ActionListResponse> {
    return this.request<ActionListResponse>(`/devices/${deviceId}/actions/`);
  }

  // Get a single action
  async getAction(deviceId: string, actionId: string): Promise<Action> {
    return this.request<Action>(`/devices/${deviceId}/actions/${actionId}`);
  }

  // Create a new action
  async createAction(deviceId: string, action: ActionCreate): Promise<Action> {
    return this.request<Action>(`/devices/${deviceId}/actions/`, {
      method: 'POST',
      body: JSON.stringify(action),
    });
  }

  // Update an existing action
  async updateAction(deviceId: string, actionId: string, updates: ActionUpdate): Promise<Action> {
    return this.request<Action>(`/devices/${deviceId}/actions/${actionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete an action
  async deleteAction(deviceId: string, actionId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/devices/${deviceId}/actions/${actionId}`, {
      method: 'DELETE',
    });
  }

  // Upload picture for an action
  async uploadPicture(deviceId: string, actionId: string, picture: File): Promise<FileUploadResponse> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();

    const formData = new FormData();
    formData.append('picture', picture);

    const response = await fetch(`${API_BASE_URL}/devices/${deviceId}/actions/${actionId}/upload-picture`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Picture upload failed' }));
      throw new Error(error.detail || 'Picture upload failed');
    }

    return response.json();
  }

  // Upload sound for an action
  async uploadSound(deviceId: string, actionId: string, sound: File): Promise<FileUploadResponse> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();

    const formData = new FormData();
    formData.append('sound', sound);

    const response = await fetch(`${API_BASE_URL}/devices/${deviceId}/actions/${actionId}/upload-sound`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Sound upload failed' }));
      throw new Error(error.detail || 'Sound upload failed');
    }

    return response.json();
  }

  // Get signed URL for action picture (for direct Firebase Storage access)
  async getSignedPictureUrl(
    deviceId: string,
    actionId: string,
    thumbnail: boolean = true
  ): Promise<SignedUrlResponse> {
    const query = thumbnail ? '?thumbnail=true' : '';
    return this.request<SignedUrlResponse>(
      `/devices/${deviceId}/actions/${actionId}/picture-url${query}`
    );
  }

  // Get signed URLs for multiple action pictures in a single request
  async getBatchSignedPictureUrls(
    deviceId: string,
    actionIds: string[],
    thumbnail: boolean = true
  ): Promise<BatchSignedUrlsResponse> {
    return this.request<BatchSignedUrlsResponse>(
      `/devices/${deviceId}/actions/picture-urls`,
      {
        method: 'POST',
        body: JSON.stringify({ action_ids: actionIds, thumbnail }),
      }
    );
  }
}

export const actionsService = new ActionsService();
